// src/components/SumerianHero.jsx
import React, {useEffect, useRef, useState} from 'react';
import { ArrowRight, Zap } from 'lucide-react';

const SumerianHero = ({ heroData, onPrimaryClick, onSecondaryClick }) => {
    const canvasRef = useRef(null);
    const animationRef = useRef(null);
    const triggerLightningRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');

        // Mobile performance optimization
        const isMobile = window.innerWidth < 768;
        const isLowEndDevice = navigator.hardwareConcurrency <= 4 ||
            /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

        // Reduce DPR on mobile for performance
        const dpr = isMobile ? Math.min(window.devicePixelRatio || 1, 1.5) : (window.devicePixelRatio || 1);

        // Set canvas size to match container
        const resizeCanvas = () => {
            const rect = canvas.getBoundingClientRect();
            canvas.width = rect.width * dpr;
            canvas.height = rect.height * dpr;
            ctx.scale(dpr, dpr);
            return { width: rect.width, height: rect.height };
        };

        let { width, height } = resizeCanvas();


        // Lightning flash system for dramatic background illumination
        const lightningFlashes = {
            nextFlash: Math.random() * 5000 + 3000, // Random time between 3-8 seconds
            flashDuration: 0,
            flashIntensity: 0,
            isFlashing: false,
            flashStart: 0,
            lightningPath: null
        };

        // Generate a branch with potential sub-branches
        const generateBranch = (startPoint, progressRatio, width, height, generation) => {
            const maxGenerations = 5; // Maximum branching depth
            if (generation > maxGenerations) return [];

            const branch = [{ ...startPoint }];
            const branchLength = Math.floor((4 - generation) * (2 + Math.random() * 4)); // Shorter branches for deeper generations
            const angleVariation = (Math.random() - 0.5) * Math.PI * 0.8; // More varied angles
            const branchSpeed = 0.7 + Math.random() * 0.6; // How fast the branch moves away from main bolt

            let currentX = startPoint.x;
            let currentY = startPoint.y;

            for (let i = 1; i < branchLength; i++) {
                // Branch direction influenced by generation and randomness
                const baseAngle = generation === 1 ? angleVariation : angleVariation * 0.7;
                const deltaX = Math.cos(baseAngle) * (20 + Math.random() * 40) * branchSpeed;
                const deltaY = Math.sin(Math.PI * 0.3) * (15 + Math.random() * 25) + (Math.random() * 20); // Tend downward

                currentX += deltaX;
                currentY += deltaY;

                // Keep within reasonable bounds
                currentX = Math.max(-100, Math.min(width + 100, currentX));

                const branchPoint = { x: currentX, y: currentY };
                branch.push(branchPoint);

                // Generate sub-branches (recursive branching)
                if (i > 1 && generation < maxGenerations && Math.random() < (0.4 / generation)) {
                    const subBranch = generateBranch(branchPoint, progressRatio, width, height, generation + 1);
                    if (subBranch.length > 1) {
                        // Store sub-branches as nested structure
                        if (!branch.subBranches) branch.subBranches = [];
                        branch.subBranches.push(subBranch);
                    }
                }
            }

            return branch;
        };

        // Generate complex lightning strikes with recursive branching
        const generateLightningStrike = (width, height) => {
            const lightning = {
                mainPath: [],
                branches: []
            };

            // Main lightning bolt
            const startX = Math.random() * width;
            const startY = -50 - Math.random() * 100;
            const segments = 12 + Math.floor(Math.random() * 4);

            let currentX = startX;
            let currentY = startY;
            lightning.mainPath.push({ x: currentX, y: currentY });

            for (let i = 1; i < segments; i++) {
                const deltaX = (Math.random() - 0.5) * 80;
                const deltaY = (height + 200) / segments;

                currentX += deltaX;
                currentY += deltaY;
                currentX = Math.max(-50, Math.min(width + 50, currentX));

                const point = { x: currentX, y: currentY };
                lightning.mainPath.push(point);

                // Generate branches from main path points
                if (i > 2 && i < segments - 2 && Math.random() < 0.6) {
                    const branch = generateBranch(point, i / segments, width, height, 1);
                    if (branch.length > 1) {
                        lightning.branches.push(branch);
                    }
                }
            }

            return lightning;
        };

        // Trigger lightning manually (for mouse clicks)
        const triggerLightning = (width, height) => {
            if (!lightningFlashes.isFlashing) {
                lightningFlashes.isFlashing = true;
                lightningFlashes.flashDuration = 150 + Math.random() * 100; // Slightly faster for manual triggers
                lightningFlashes.flashIntensity = 0.9 + Math.random() * 0.1; // Higher intensity for manual
                lightningFlashes.flashStart = time; // Use the same time reference as the animation loop
                lightningFlashes.lightningPath = generateLightningStrike(width, height);

                // Schedule next automatic flash further in the future to avoid conflicts
                lightningFlashes.nextFlash = time + Math.random() * 10000 + 6000; // 6-16 seconds
            }
        };

        triggerLightningRef.current = triggerLightning;

        // Draw complex lightning strikes with recursive branching
        const drawLightningStrikes = (ctx, width, height, time) => {
            // Check if it's time for a new flash (automatic)
            if (!lightningFlashes.isFlashing && time > lightningFlashes.nextFlash) {
                lightningFlashes.isFlashing = true;
                lightningFlashes.flashDuration = 200 + Math.random() * 300; // 200-500ms flash
                lightningFlashes.flashIntensity = 0.8 + Math.random() * 0.2; // 0.8-1.0 intensity
                lightningFlashes.flashStart = time;
                lightningFlashes.lightningPath = generateLightningStrike(width, height);

                // Schedule next flash
                lightningFlashes.nextFlash = time + Math.random() * 8000 + 4000; // 4-12 seconds
            }

            // Handle active flash
            if (lightningFlashes.isFlashing) {
                const flashAge = time - lightningFlashes.flashStart;
                const flashProgress = flashAge / lightningFlashes.flashDuration;

                if (flashProgress >= 1) {
                    lightningFlashes.isFlashing = false;
                    lightningFlashes.flashIntensity = 0;
                    lightningFlashes.lightningPath = null; // Clear the path
                    return 0; // Return flash overlay intensity
                }

                // Flash intensity curve (quick flash, slower fade)
                const intensity = flashProgress < 0.1
                    ? lightningFlashes.flashIntensity * (flashProgress / 0.1)
                    : lightningFlashes.flashIntensity * (1 - ((flashProgress - 0.1) / 0.9));

                // Draw the complex lightning structure
                if (lightningFlashes.lightningPath) {
                    ctx.save();

                    // Function to draw a branch recursively
                    const drawBranch = (branch, generation = 1) => {
                        if (!branch || branch.length < 2) return;

                        // Adjust line width based on generation (thinner for sub-branches)
                        const baseWidth = 10;
                        const lineWidth = Math.max(1, baseWidth / Math.pow(1.5, generation - 1));
                        ctx.lineWidth = lineWidth + Math.random() * 2;

                        // Adjust opacity based on generation
                        const generationOpacity = Math.pow(0.8, generation - 1);
                        ctx.globalAlpha = intensity * generationOpacity;

                        // Main branch path
                        ctx.beginPath();
                        ctx.moveTo(branch[0].x, branch[0].y);
                        for (let i = 1; i < branch.length; i++) {
                            ctx.lineTo(branch[i].x, branch[i].y);
                        }
                        ctx.stroke();

                        // Draw sub-branches recursively
                        if (branch.subBranches) {
                            branch.subBranches.forEach(subBranch => {
                                drawBranch(subBranch, generation + 1);
                            });
                        }
                    };

                    // Set up lightning drawing style
                    ctx.strokeStyle = '#ffffff';
                    ctx.lineCap = 'round';
                    ctx.lineJoin = 'round';
                    ctx.shadowColor = '#60a5fa';
                    ctx.shadowBlur = 25;

                    // Draw main lightning bolt
                    ctx.lineWidth = 10 + Math.random() * 6;
                    ctx.globalAlpha = intensity;

                    ctx.beginPath();
                    ctx.moveTo(lightningFlashes.lightningPath.mainPath[0].x, lightningFlashes.lightningPath.mainPath[0].y);
                    lightningFlashes.lightningPath.mainPath.forEach((point, index) => {
                        if (index > 0) {
                            ctx.lineTo(point.x, point.y);
                        }
                    });
                    ctx.stroke();

                    // Draw all branches recursively
                    lightningFlashes.lightningPath.branches.forEach(branch => {
                        drawBranch(branch, 1);
                    });

                    ctx.restore();
                }

                return intensity * 0.35; // Return flash overlay intensity for background
            }

            return 0;
        };

        // Authentic Cuneiform symbols from SVG data plus additional variations
        const cuneiformSymbols = [
            // First authentic glyph from SVG
            {
                paths: [
                    "M 256.51,778.5 C 255.14,762.45 252.58,747.09 248.83,732.42 C 198.99,679.17 158.03,634.37 125.95,598.02 C 93.86,561.67 66.73,533.43 44.54,513.28 C 58.53,498.94 76.71,481.37 99.07,460.54 C 121.42,439.72 145.66,415.48 171.77,387.84 C 197.88,360.19 223.23,329.3 247.8,295.17 C 252.23,279.13 255.14,262.58 256.5,245.51 C 274.24,256.77 300.44,270.01 335.09,285.19 C 369.73,300.38 410.52,316.85 457.46,334.6 C 504.39,352.35 555.42,370.7 610.55,389.64 C 665.67,408.58 722.59,427.45 781.3,446.22 C 794.61,433.93 808.26,420.62 822.26,406.28 C 836.25,391.94 849.56,377.44 862.2,362.76 L 876.54,370.44 C 860.16,399.8 846.16,425.91 834.56,448.78 C 822.95,471.65 811.69,492.13 800.77,510.22 C 808.62,523.53 819.63,540.68 833.79,561.68 C 847.95,582.67 864.93,606.14 884.73,632.08 L 871.42,642.83 C 856.06,629.18 841.8,616.81 828.67,605.71 C 815.53,594.62 803.33,584.29 792.06,574.73 C 744.27,589.75 697.51,605.03 651.77,620.55 C 606.03,636.09 562.51,651.27 521.21,666.12 C 479.91,680.97 441.93,695.22 407.29,708.87 C 372.64,722.52 342.43,735.24 316.67,747.02 C 290.89,758.8 270.85,769.29 256.51,778.5 Z M 228.35,672.51 C 253.61,671.83 284.93,668.59 322.3,662.78 C 359.68,656.98 401.99,649.21 449.28,639.48 C 496.55,629.75 547.84,618.32 603.14,605.18 C 658.44,592.04 716.46,577.79 777.22,562.43 C 764.93,552.53 753.32,543.23 742.4,534.53 C 731.47,525.83 720.72,517.72 710.14,510.21 C 716.96,504.41 725.41,497.15 735.48,488.45 C 745.54,479.75 756.38,469.94 767.99,459.01 C 689.48,439.22 616.09,421.55 547.83,406.02 C 479.56,390.49 418.72,377.95 365.3,368.39 C 311.88,358.84 268.28,353.37 234.48,352.01 C 217.07,385.47 200.43,414.73 184.56,439.82 C 168.69,464.91 154.78,489.4 142.83,513.29 C 151.7,530.36 163.22,552.55 177.39,579.85 C 191.55,607.16 208.53,638.05 228.33,672.52 Z M 916.99,727.3 C 916.99,707.84 916.22,685.32 914.69,659.72 C 913.15,634.12 911.11,607.07 908.55,578.57 C 906,550.07 903.09,521.48 899.85,492.81 C 896.6,464.14 893.19,437 889.61,411.4 C 886.03,385.8 882.44,363.19 878.86,343.56 C 875.28,323.94 871.95,309.83 868.88,299.25 C 886.29,303.35 904.21,305.39 922.64,305.39 C 941.07,305.39 960.02,303.34 979.47,299.25 C 976.4,309.83 973.07,324.94 969.49,344.56 C 965.91,364.19 962.23,386.8 958.48,412.4 C 954.72,438 951.22,465.14 947.98,493.81 C 944.73,522.48 941.75,551.07 939.02,579.57 C 936.28,608.07 934.16,635.12 932.62,660.72 C 931.08,686.32 930.32,708.85 930.32,728.3 H 916.99 Z"
                ]
            },
            // Second authentic glyph from SVG
            {
                paths: [
                    "M 499.83,780.15 C 495.02,765.12 488.49,750.99 480.23,737.79 C 471.96,724.57 461.22,711.35 448.01,698.13 C 459.12,694.82 472.42,690.47 487.89,685.06 C 503.36,679.65 520.26,673.19 538.59,665.68 C 517.86,644.65 496.68,623.92 475.05,603.49 C 453.42,583.06 432.08,563.54 411.06,544.9 C 409.86,544.9 408.58,544.98 407.23,545.13 C 405.88,545.28 404.45,545.35 402.95,545.35 C 381.92,572.39 363.14,597.71 346.62,621.29 C 330.09,644.88 317.33,665.98 308.31,684.61 C 299.3,671.39 289,659.68 277.44,649.46 C 265.87,639.25 251.98,629.48 235.75,620.17 C 246.57,612.96 259.86,603.5 275.63,591.78 C 291.4,580.06 308.6,566.54 327.23,551.22 C 298.68,553.33 272.92,555.5 249.94,557.75 C 226.96,560 207.95,562.19 192.93,564.29 C 177.9,566.4 168.29,568.5 164.09,570.6 C 164.99,561.89 165.74,552.2 166.34,541.53 C 166.94,530.87 167.24,520.12 167.24,509.31 C 167.24,498.5 166.94,488.58 166.34,478.66 C 165.73,468.75 164.99,460.34 164.09,453.42 C 169.19,455.83 179.48,458.31 194.96,460.86 C 210.43,463.42 229.81,465.97 253.1,468.52 C 276.38,471.08 302.29,473.56 330.84,475.96 C 290.88,443.51 259.18,419.48 235.75,403.85 C 251.97,394.54 266.87,384.7 278.44,374.33 C 290,363.96 300.3,352.32 309.31,339.4 C 318.32,358.33 331.32,379.89 348.29,404.07 C 365.26,428.26 384.72,454.17 406.65,481.81 C 407.25,481.81 407.7,481.89 408,482.04 C 408.3,482.2 408.6,482.27 408.9,482.27 C 430.23,463.34 452.01,443.44 474.25,422.56 C 496.48,401.68 518.11,380.58 539.15,359.24 C 520.82,351.43 504,344.75 488.68,339.19 C 473.36,333.63 460.13,329.2 449.02,325.9 C 462.24,312.68 472.98,299.47 481.24,286.24 C 489.5,273.02 496.03,258.9 500.84,243.88 C 516.16,261.01 536.67,279.79 562.36,301.21 C 588.05,322.65 617.04,344.28 649.34,366.11 C 681.63,388.95 715.88,412.53 752.09,436.86 C 788.29,461.2 824.57,485.53 860.93,509.87 L 860.03,510.32 H 860.93 V 522.49 C 824.57,546.83 788.3,570.79 752.09,594.37 C 715.88,617.96 681.63,640.79 649.34,662.87 C 617.04,684.95 588.05,705.91 562.36,725.74 C 536.67,745.57 516.17,764.05 500.84,781.17 Z M 511.1,489.47 C 509.9,475.95 508.4,463.71 506.59,452.74 C 504.79,441.78 502.83,432.99 500.73,426.38 C 492.31,436.29 483.98,446.21 475.72,456.12 C 467.45,466.03 459.42,475.8 451.61,485.41 L 511.1,489.47 Z M 522.37,623.77 C 521.17,611.45 520.12,597.93 519.22,583.21 C 518.32,568.49 517.11,553.62 515.61,538.59 C 505.39,538.89 495.18,539.42 484.96,540.17 C 474.74,540.92 464.53,541.6 454.31,542.2 C 465.13,555.42 476.24,568.87 487.66,582.53 C 499.08,596.2 510.64,609.95 522.37,623.77 Z M 838.73,508.39 C 805.98,489.17 772.78,470.84 739.13,453.41 C 705.48,435.99 672.8,419.76 641.11,404.74 C 609.41,389.72 579.74,376.5 552.1,365.08 L 503.43,423.22 C 507.04,423.52 510.56,423.9 514.02,424.35 C 517.47,424.8 521.15,425.03 525.06,425.03 C 533.47,425.03 542.33,424.13 551.65,422.33 C 549.54,427.44 547.52,436.23 545.57,448.69 C 543.61,461.16 541.89,475.36 540.39,491.28 C 580.95,493.69 620.23,496.01 658.24,498.27 C 696.24,500.52 730.87,502.48 762.12,504.13 C 793.36,505.78 818.9,507.21 838.73,508.41 Z M 553.01,660.27 C 580.35,649.16 609.72,636.46 641.12,622.19 C 672.51,607.92 704.89,592.38 738.24,575.55 C 771.59,558.73 804.49,540.99 836.94,522.37 C 817.41,522.98 791.94,524.02 760.55,525.52 C 729.15,527.03 694.22,528.75 655.77,530.7 C 617.31,532.66 577.65,534.83 536.79,537.24 C 535.59,554.97 534.46,572.32 533.41,589.29 C 532.35,606.27 531.53,621.22 530.93,634.13 C 538.44,642.24 545.8,650.96 553.01,660.27 Z"
                ]
            },
        ];



        // Enhanced floating symbols with more dynamic behavior
        const floatingSymbols = [];
        const maxSymbols = 12; // Increased count

        class FloatingSymbol {
            constructor() {
                this.symbol = cuneiformSymbols[Math.floor(Math.random() * cuneiformSymbols.length)];
                this.x = Math.random() * width;
                this.y = Math.random() * height;
                this.vx = (Math.random() - 0.5) * 0.3; // Slightly faster movement
                this.vy = (Math.random() - 0.5) * 0.3;
                this.rotation = Math.random() * Math.PI * 2;
                this.rotationSpeed = (Math.random() - 0.5) * 0.008;
                this.opacity = 0.1 + Math.random() * 0.15; // More visible
                this.scale = 0.5 + Math.random() * 0.6; // Larger symbols
                this.pulse = Math.random() * Math.PI * 2;
                this.pulseSpeed = 0.02 + Math.random() * 0.015;
                this.depth = Math.random(); // For layering effect
            }

            update() {
                this.x += this.vx;
                this.y += this.vy;
                this.rotation += this.rotationSpeed;
                this.pulse += this.pulseSpeed;

                // Wrap around edges with padding
                if (this.x < -80) this.x = width + 80;
                if (this.x > width + 80) this.x = -80;
                if (this.y < -80) this.y = height + 80;
                if (this.y > height + 80) this.y = -80;
            }

            draw(ctx) {
                ctx.save();
                ctx.translate(this.x, this.y);
                ctx.rotate(this.rotation);
                ctx.scale(this.scale, this.scale);

                const dynamicOpacity = this.opacity + Math.sin(this.pulse) * 0.05;
                ctx.globalAlpha = Math.max(0, dynamicOpacity);

                // Simplified rendering on mobile
                if (isMobile) {
                    ctx.fillStyle = `rgba(96, 165, 250, ${dynamicOpacity})`;
                    ctx.strokeStyle = `rgba(96, 165, 250, ${dynamicOpacity})`;
                    ctx.lineWidth = 1;
                } else {
                    // Enhanced glow effect based on depth (desktop only)
                    const glowIntensity = this.depth > 0.5 ? 1.2 : 0.8;
                    ctx.fillStyle = `rgba(96, 165, 250, ${dynamicOpacity * glowIntensity})`;
                    ctx.strokeStyle = `rgba(96, 165, 250, ${dynamicOpacity * glowIntensity})`;
                    ctx.lineWidth = 1.5;

                    // Add shadow for depth (desktop only)
                    ctx.shadowColor = '#60a5fa';
                    ctx.shadowBlur = this.depth * 8;
                }

                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';

                this.symbol.paths.forEach(pathData => {
                    if (pathData.includes('M') && pathData.includes('L')) {
                        const path = new Path2D(pathData);
                        if (pathData.includes('Z')) {
                            ctx.fill(path);
                        } else {
                            ctx.stroke(path);
                        }
                    }
                });

                ctx.restore();
            }
        }

        // Initialize floating symbols
        for (let i = 0; i < maxSymbols; i++) {
            floatingSymbols.push(new FloatingSymbol());
        }

        // Enhanced static symbols with mobile optimization
        const staticSymbolCount = isMobile ? 2 : 6; // Fewer static symbols on mobile
        const staticSymbols = [];

        for (let i = 0; i < staticSymbolCount; i++) {
            const symbolIndex = i % cuneiformSymbols.length;
            staticSymbols.push({
                symbol: cuneiformSymbols[symbolIndex],
                x: width * (0.1 + (i / staticSymbolCount) * 0.8),
                y: height * (0.2 + (i % 2) * 0.6),
                scale: isMobile ? 0.8 + Math.random() * 0.3 : 1.4 + Math.random() * 0.4,
                opacity: isMobile ? 0.08 + Math.random() * 0.02 : 0.18 + Math.random() * 0.05,
                rotation: (Math.random() - 0.5) * 0.3
            });
        }

        // Lightning bolts with mobile optimization
        const lightningBoltCount = isMobile ? 1 : 3; // Fewer lightning bolts on mobile
        const lightningBolts = [];

        for (let i = 0; i < lightningBoltCount; i++) {
            lightningBolts.push({
                x: width * (0.2 + i * 0.6),
                y: height * (0.2 + (i % 2) * 0.6),
                path: [
                    { x: 0, y: 0 },
                    { x: 15, y: 25 },
                    { x: -5, y: 30 },
                    { x: 10, y: 55 }
                ]
            });
        }

        // Draw enhanced static symbols
        const drawStaticSymbols = (ctx, time) => {
            staticSymbols.forEach(staticSymbol => {
                ctx.save();
                ctx.translate(staticSymbol.x, staticSymbol.y);
                ctx.rotate(staticSymbol.rotation + Math.sin(time * 0.0008) * 0.08);
                ctx.scale(staticSymbol.scale, staticSymbol.scale);

                const dynamicOpacity = staticSymbol.opacity + Math.sin(time * 0.003) * 0.04;
                ctx.globalAlpha = Math.max(0, dynamicOpacity);

                ctx.fillStyle = '#60a5fa';
                ctx.strokeStyle = '#60a5fa';
                ctx.lineWidth = 1.5;
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';
                ctx.shadowColor = '#60a5fa';
                ctx.shadowBlur = 6;

                staticSymbol.symbol.paths.forEach(pathData => {
                    if (pathData.includes('M') && pathData.includes('L')) {
                        const path = new Path2D(pathData);
                        if (pathData.includes('Z')) {
                            ctx.fill(path);
                        } else {
                            ctx.stroke(path);
                        }
                    }
                });

                ctx.restore();
            });
        };

        // Enhanced geometric pattern
        const drawGeometricPattern = (ctx, width, height, time) => {
            ctx.save();
            ctx.globalAlpha = 0.06;
            ctx.strokeStyle = '#60a5fa';
            ctx.lineWidth = 1;

            const gridSize = 80;
            const offset = (time * 0.015) % gridSize;

            for (let x = -gridSize + offset; x < width + gridSize; x += gridSize) {
                for (let y = -gridSize + offset; y < height + gridSize; y += gridSize) {
                    const variation = Math.sin(x * 0.01 + time * 0.001) * Math.cos(y * 0.01 + time * 0.001);
                    const size = gridSize * (0.3 + variation * 0.2);

                    ctx.beginPath();
                    ctx.moveTo(x + size/2, y);
                    ctx.lineTo(x + size, y + size/2);
                    ctx.lineTo(x + size/2, y + size);
                    ctx.lineTo(x, y + size/2);
                    ctx.closePath();
                    ctx.stroke();

                    if (variation > 0.3) {
                        ctx.beginPath();
                        ctx.arc(x + size/2, y + size/2, 2, 0, Math.PI * 2);
                        ctx.fill();
                    }
                }
            }

            ctx.restore();
        };

        // Enhanced lightning accents with randomized movement
        const drawLightningAccents = (ctx, width, height, time) => {
            ctx.save();
            ctx.strokeStyle = '#60a5fa';
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.shadowColor = '#60a5fa';
            ctx.shadowBlur = 4;

            lightningBolts.forEach((bolt, index) => {
                const opacity = 0.3 + Math.sin(time * 0.004 + index) * 0.25;
                ctx.globalAlpha = Math.max(0.05, opacity);

                // Add subtle position variation
                const xOffset = Math.sin(time * 0.002 + index * 2) * 10;
                const yOffset = Math.cos(time * 0.0015 + index * 3) * 8;

                ctx.beginPath();
                ctx.moveTo(bolt.x + xOffset, bolt.y + yOffset);

                bolt.path.forEach(point => {
                    ctx.lineTo(bolt.x + point.x + xOffset, bolt.y + point.y + yOffset);
                });

                ctx.stroke();
            });

            ctx.restore();
        };

        // Animation loop with mobile frame rate optimization and lightning flashes
        let time = 0;
        let lastFrameTime = 0;
        const targetFPS = isMobile ? 30 : 60; // Lower FPS on mobile
        const frameInterval = 1000 / targetFPS;

        const animate = (currentTime) => {

            if (currentTime - lastFrameTime >= frameInterval) {
                ctx.clearRect(0, 0, width, height);

                // Draw background geometric pattern (restored on desktop)
                drawGeometricPattern(ctx, width, height, time);

                // Draw static prominent symbols
                drawStaticSymbols(ctx, time);

                // Update and draw floating symbols
                if (isMobile) {
                    // Simpler rendering on mobile - no depth sorting
                    floatingSymbols.forEach(symbol => {
                        symbol.update();
                        symbol.draw(ctx);
                    });
                } else {
                    // Full rendering on desktop with depth sorting
                    floatingSymbols
                        .sort((a, b) => a.depth - b.depth)
                        .forEach(symbol => {
                            symbol.update();
                            symbol.draw(ctx);
                        });
                }

                // Draw subtle lightning accents
                drawLightningAccents(ctx, width, height, time);

                // Draw dramatic lightning strikes and get flash intensity
                const flashIntensity = !isMobile ? drawLightningStrikes(ctx, width, height, time) : 0;

                // Apply flash overlay to entire canvas
                if (flashIntensity > 0) {
                    ctx.save();
                    ctx.globalAlpha = flashIntensity;
                    ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
                    ctx.fillRect(0, 0, width, height);
                    ctx.restore();
                }

                time += frameInterval;
                lastFrameTime = currentTime;
            }

            animationRef.current = requestAnimationFrame(animate);
        };

        animate(0);

        // Handle resize with mobile considerations
        const handleResize = () => {
            const newSize = resizeCanvas();
            width = newSize.width;
            height = newSize.height;

            // Recreate mobile-optimized elements on resize
            const newIsMobile = window.innerWidth < 768;
            if (newIsMobile !== isMobile) {
                // Restart animation with new mobile settings if device type changed
                window.location.reload();
                return;
            }

            // Reposition static elements on resize
            staticSymbols.forEach((symbol, index) => {
                symbol.x = width * (0.1 + (index / staticSymbols.length) * 0.8);
                symbol.y = height * (0.2 + (index % 2) * 0.6);
            });

            // Reposition lightning bolts
            lightningBolts.forEach((bolt, index) => {
                bolt.x = width * (0.2 + index * 0.6);
                bolt.y = height * (0.2 + (index % 2) * 0.6);
            });
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, []);


    function handleSectionClick() {
        if (triggerLightningRef.current) {
            // Get canvas dimensions for lightning generation
            const canvas = canvasRef.current;
            if (canvas) {
                const rect = canvas.getBoundingClientRect();
                triggerLightningRef.current(rect.width, rect.height);
            }
        }
    }

    return (
        <section
            className="relative bg-gray-900 overflow-hidden min-h-screen flex items-center"
            aria-labelledby="hero-heading"
            onClick={handleSectionClick}

        >
            {/* Enhanced Animated Sumerian Background with Lightning Flash Overlay */}
            <canvas
                ref={canvasRef}
                className="absolute inset-0 w-full h-full"
                style={{ zIndex: 1 }}
                aria-hidden="true"
            />

            {/* Gradient Overlay - slightly more transparent to show lightning flashes */}
            <div className="absolute inset-0 bg-gradient-to-br from-gray-900/90 via-gray-800/85 to-gray-900/90" style={{ zIndex: 2 }}></div>

            {/* Content */}
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32" style={{ zIndex: 3 }}>
                <div className="text-center">
                    {/* Mobile-Optimized Sumerian Etymology */}
                    <div className="flex flex-col sm:flex-row justify-center items-center space-y-2 sm:space-y-0 sm:space-x-4 mb-6 sm:mb-8">
                        <div className="flex items-center space-x-2 text-blue-400 bg-gray-800/60 px-3 sm:px-4 py-2 rounded-lg backdrop-blur-sm border border-gray-700/50">
                            <div className="relative">
                                <Zap className="h-5 w-5 sm:h-6 sm:w-6 animate-pulse" />

                            </div>
                            <span className="text-xs sm:text-sm font-medium tracking-wider uppercase">
                                barāqu
                            </span>
                        </div>
                        <div className="hidden sm:block text-gray-500 text-sm">→</div>
                        <div className="text-blue-300 text-xs sm:text-sm italic text-center">
                            "to flash like lightning, to shine"
                        </div>
                    </div>

                    {/* Mobile-Optimized Main Title */}
                    <h1
                        id="hero-heading"
                        className="text-4xl sm:text-5xl md:text-7xl font-light text-white mb-4 sm:mb-6 tracking-tight"
                    >
                        <span className="font-bold">{heroData?.name?.split(' ')[0] || 'Baraqu'}</span>{' '}
                        <span className="text-blue-400 font-normal">{heroData?.name?.split(' ')[1] || 'LLC'}</span>
                    </h1>


                    {/* Mobile-Optimized Subtitle */}
                    <div className="mb-6 sm:mb-8">
                        <p className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-3 sm:mb-4 max-w-4xl mx-auto leading-relaxed px-2">
                            {heroData?.title || "Technology Solutions - Flashing Like Lightning"}
                        </p>
                        <div className="flex justify-center">
                            <div className="bg-gray-800/40 backdrop-blur-sm border border-gray-700/50 rounded-lg px-4 sm:px-6 py-2 sm:py-3 max-w-xs sm:max-w-none">
                                <p className="text-xs sm:text-sm text-blue-300 font-medium">
                                    From the first recorded data to tomorrow's innovations
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Mobile-Optimized Description */}
                    <p className="text-base sm:text-lg text-gray-400 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed px-2">
                        Just as Sumerian cuneiform gave birth to written language and systematic data storage,
                        we illuminate the path forward through cutting-edge technology solutions that spark progress and evolution.
                    </p>

                    {/* Mobile-Optimized CTA Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4 sm:px-0">
                        <button
                            onClick={onPrimaryClick}
                            className="group inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 transform hover:scale-105 w-full sm:w-auto"
                        >
                            {heroData?.cta?.primary?.text || "Explore Solutions"}
                            <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform duration-200" />
                        </button>
                        <button
                            onClick={onSecondaryClick}
                            className="group inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 border border-gray-600 text-gray-300 font-medium rounded-lg hover:border-blue-400 hover:text-blue-400 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-900 backdrop-blur-sm bg-gray-800/20 w-full sm:w-auto"
                        >
                            {heroData?.cta?.secondary?.text || "Our Story"}
                            <div className="ml-2 w-1.5 h-1.5 sm:w-2 sm:h-2 bg-current rounded-full opacity-50 group-hover:opacity-100 transition-opacity duration-200"></div>
                        </button>
                    </div>

                    {/* Mobile-Optimized Ancient Wisdom Quote */}
                    <div className="mt-12 sm:mt-16 max-w-2xl mx-auto px-4">
                        <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700/30 rounded-lg p-4 sm:p-6">
                            <blockquote className="text-gray-300 italic text-sm sm:text-base leading-relaxed">
                                "Like the ancient scribes who created humanity's first systematic approach to information,
                                we bridge the wisdom of millennia with the innovations of tomorrow."
                            </blockquote>
                            <div className="mt-3 flex items-center justify-center space-x-2 text-xs text-blue-400">
                                <div className="w-6 sm:w-8 h-0.5 bg-blue-400/50"></div>
                                <span className="text-center">Systematic Innovation Since 3200 BCE</span>
                                <div className="w-6 sm:w-8 h-0.5 bg-blue-400/50"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default SumerianHero;